import {
  ArrowLeft,
  BellRing,
  BookOpenCheck,
  BrainCircuit,
  CandlestickChart,
  DatabaseZap,
  GraduationCap,
  Link2,
  Radar,
  Scale,
  Search,
  ShieldCheck,
  Smartphone,
  Telescope,
  WalletCards,
  Waves,
  type LucideIcon,
} from "lucide-react";
import type { CSSProperties } from "react";
import { usePreview } from "@tradvera/design-system";
import "./tradvera-landing.css";

type Screen = "home" | "academy" | "trade" | "bridge";
type TradeView = "command" | "research" | "company-evaluation" | "opportunity" | "my-portfolio" | "production" | "technical" | "decision";
type LandingProps = { onNavigate: (screen: Screen) => void; onOpenTrade: (view: TradeView) => void };

const quickTools: ReadonlyArray<{
  view: TradeView;
  ar: string;
  en: string;
  hintAr: string;
  hintEn: string;
  icon: LucideIcon;
}> = [
  { view: "command", ar: "سوق اليوم", en: "Market Command", hintAr: "نبض السوق والملخص اليومي", hintEn: "Market pulse and daily brief", icon: Radar },
  { view: "opportunity", ar: "مركز الفرص", en: "Opportunity Center", hintAr: "المرشحون والعدسات الثلاث", hintEn: "Candidates and three horizons", icon: Telescope },
  { view: "decision", ar: "غرفة القرار", en: "Decision Room", hintAr: "مراقبة، شراء، أو تجاهل", hintEn: "Watch, bought, or dismiss", icon: Scale },
  { view: "research", ar: "تحليل شركة", en: "Company Research", hintAr: "التحليل المستقل الكامل", hintEn: "Full independent research", icon: Search },
  { view: "company-evaluation", ar: "قيّم شركتك", en: "Evaluate Company", hintAr: "الحكم المتكامل في خمس غرف", hintEn: "Five-room integrated judgment", icon: BrainCircuit },
  { view: "technical", ar: "المرصد التقني", en: "Technical Observatory", hintAr: "الاتجاه والزخم وشروط الدخول", hintEn: "Trend, momentum, and conditions", icon: Waves },
  { view: "my-portfolio", ar: "محفظتي", en: "My Portfolio", hintAr: "المراكز المملوكة فعليًا", hintEn: "Your actual owned positions", icon: WalletCards },
  { view: "production", ar: "سجل الأدلة", en: "Evidence Ledger", hintAr: "المصادر والتشغيل الفعلي", hintEn: "Sources and production runs", icon: DatabaseZap },
];

const pulseBars = [34, 51, 43, 67, 58, 78, 61, 86, 72, 92, 76, 84, 69, 88, 80, 95, 73, 89] as const;

export function TradveraLanding({ onNavigate, onOpenTrade }: LandingProps) {
  const { preferences } = usePreview();
  const ar = preferences.locale === "ar";

  return (
    <main className="tv-home" aria-labelledby="tv-home-title">
      <div className="tv-home-grid-bg" aria-hidden="true" />
      <div className="tv-home-orbit orbit-one" aria-hidden="true" />
      <div className="tv-home-orbit orbit-two" aria-hidden="true" />

      <header className="tv-home-welcome">
        <div>
          <span className="tv-home-kicker"><i /> {ar ? "الرئيسية · كل البرنامج أمامك" : "HOME · YOUR WHOLE WORKSPACE"}</span>
          <h1 id="tv-home-title">{ar ? "ماذا تريد أن تفعل الآن؟" : "What do you want to do now?"}</h1>
          <p>{ar ? "ادخل مباشرة إلى أي جزء من Tradvera — بدون تمرير أو بحث." : "Open any Tradvera workspace directly — no scrolling or hunting."}</p>
        </div>
        <div className="tv-home-mark" aria-hidden="true">
          <i /><i />
          <img src="/brand/tradvera-mark.svg" alt="" />
        </div>
      </header>

      <section className="tv-home-body">
        <div className="tv-home-worlds" aria-label={ar ? "المداخل الرئيسية" : "Primary entrances"}>
          <button className="tv-world-card is-academy" onClick={() => onNavigate("academy")}>
            <span className="tv-world-icon"><GraduationCap /></span>
            <span className="tv-world-copy">
              <small>01 · ACADEMY</small>
              <strong>{ar ? "الأكاديمية" : "Academy"}</strong>
              <em>{ar ? "تعلّم، راجع، وواصل مسارك" : "Learn, review, and continue your path"}</em>
            </span>
            <ArrowLeft />
          </button>

          <button className="tv-world-card is-trade" onClick={() => onOpenTrade("command")}>
            <span className="tv-world-icon"><CandlestickChart /></span>
            <span className="tv-world-copy">
              <small>02 · TRADE</small>
              <strong>{ar ? "التداول" : "Trade"}</strong>
              <em>{ar ? "السوق، الشركات، الفرص، والقرار" : "Market, companies, opportunities, and decisions"}</em>
            </span>
            <ArrowLeft />
          </button>

          <div className="tv-home-secondary">
            <button onClick={() => onNavigate("bridge")}><Link2 /><span><b>{ar ? "الشاشة المزدوجة" : "Bridge Mode"}</b><small>{ar ? "تعلّم وراقب معًا" : "Learn and monitor"}</small></span><ArrowLeft /></button>
            <button onClick={() => window.location.assign("/companion/")}><Smartphone /><span><b>{ar ? "هيرمس الهاتف" : "Hermes Companion"}</b><small>{ar ? "الشات والتنبيهات" : "Chat and alerts"}</small></span><ArrowLeft /></button>
          </div>

          <div className="tv-home-pulse" aria-hidden="true">
            <header><span><i /> TRADVERA READY</span><span>LEARN · DECIDE · TRADE</span></header>
            <div>{pulseBars.map((height, index) => <i key={index} className={index % 5 === 2 ? "is-down" : ""} style={{ "--pulse-height": `${height}%`, "--pulse-delay": `${index * 65}ms` } as CSSProperties} />)}</div>
            <footer><ShieldCheck /> <span>{ar ? "كل مساحة تفتح بحدودها وصلاحياتها الصحيحة" : "Every workspace opens with its correct boundaries"}</span></footer>
          </div>
        </div>

        <section className="tv-home-tools" aria-labelledby="tv-tools-title">
          <header>
            <div><small>DIRECT ACCESS</small><h2 id="tv-tools-title">{ar ? "دخول مباشر" : "Open directly"}</h2></div>
            <span>{ar ? "اختر الصفحة" : "Choose a page"}<ArrowLeft /></span>
          </header>
          <div className="tv-tool-grid">
            {quickTools.map(({ view, ar: labelAr, en, hintAr, hintEn, icon: Icon }, index) => (
              <button key={view} onClick={() => onOpenTrade(view)}>
                <span className="tv-tool-number">{String(index + 1).padStart(2, "0")}</span>
                <span className="tv-tool-icon"><Icon /></span>
                <span className="tv-tool-copy"><b>{ar ? labelAr : en}</b><small>{ar ? hintAr : hintEn}</small></span>
                <ArrowLeft />
              </button>
            ))}
          </div>
        </section>
      </section>

      <footer className="tv-home-footer">
        <span><BellRing /> {ar ? "هيرمس والتنبيهات يعملان من تطبيق الهاتف" : "Hermes and alerts live in the phone app"}</span>
        <span><BookOpenCheck /> {ar ? "الأكاديمية والتداول منفصلان — والعبور واضح" : "Academy and Trade stay separate — navigation stays clear"}</span>
      </footer>
    </main>
  );
}
